import {
  getAvailableTimeSlots,
  getAvailableTimeSlotsByCareProviders,
  getAvailableTimeSlotsDetail,
  getAvailableTimeSlotsDetailByCareProviders,
} from 'api/services/Form';
import CollapsableElement from 'components/CollapsableElement/CollapsableElement';
import ControlledCollapsable from 'components/ControlledCollapsable/ControlledCollapsable';
import Dropdown from 'components/Dropdown';
import Popup from 'components/Popup';
import CareProviderInfoPopup from 'components/Popup/CareProviderInfoPopup';
import VideoPopup from 'components/VideoPopup';
import useDidUpdateEffect from 'hooks/useDidUpdateEffect';
import moment from 'moment';
import { getServiceItemsWithProviders } from 'pages/Create/HomePage';
import { ChangeEvent, FC, PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { Trans, useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useMediaQuery } from 'react-responsive';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FORM_SECTION_LABELS, MAX_WIDTH_MOBILE } from 'utils/constant';
import {
  areAllTimeSlotsDisabledOrEmpty,
  formatDate,
  getAmcOnlineBookingUrl,
  getFormOrder,
  getLocalizedValue,
  isSameMonthYear,
  resetDateToMidnight,
  tzStartDay,
} from 'utils/helper';
import { ReactComponent as IconExpand } from '../../../assets/expand.svg';
import IconInfo from '../../../assets/icon_info.png';
import { ReactComponent as IconReminder } from '../../../assets/reminder.svg';
import { ReactComponent as IconVideo } from '../../../assets/video.svg';
import Button from '../../../components/Button';
import CollapsableBlock from '../../../components/CollapsableBlock';
import DatePicker from '../../../components/DatePicker';
import PatientForm from '../../../components/PatientForm';
import StepIndicator from '../../../components/StepIndicator';
import TimePicker from '../../../components/TimePicker';
import VerticalSelector from '../../../components/VerticalSelector';
import { IOption, POPUP_STATUS } from '../../../entities';
import {
  AvailableTimeslot,
  CareProvider,
  CareProviderAvailableTimeslot,
  Department,
  FORM_FLOW,
  FORM_SECTION,
  FormValues,
  GetAvailableTimeSlotsRes,
  PatientFormValues,
  ServiceItem,
} from '../../../entities/Form';
import {
  validateEHealth,
  validateEmail,
  validateHKID,
  validateName,
  validatePhoneNumber,
} from '../../../utils/validator';
import styles from './Form.module.scss';

export interface ServiceItemsWithProviders {
  serviceItem: ServiceItem;
  careProviders: CareProvider[];
}

const splitMap = {
  6: [3, 3],
  7: [4, 3],
  8: [4, 4],
  9: [5, 4],
};

interface FormProps {
  flow: FORM_FLOW | undefined;

  departmentOptions: Department[] | undefined;
  careProviderOptions: CareProvider[] | undefined;
  serviceItemOptions: ServiceItem[] | ServiceItemsWithProviders[] | undefined;

  formValues: FormValues;
  onSubmit: (availableTimeSlotsDetail: AvailableTimeslot[]) => void;
  activeFormSection: FORM_SECTION;
  patientFormNoScroll?: boolean;
  onChangeFormValues: (values: FormValues) => void;

  bookingTimeLabel: string | undefined;
  onSelectTime: (bookingTimeLabel?: string) => void;

  setIsFormValuesDirty: (isDirty: boolean) => void;
}

const Form: FC<PropsWithChildren<FormProps>> = ({
  flow,
  departmentOptions,
  careProviderOptions,
  serviceItemOptions,
  formValues,
  onSubmit,
  activeFormSection: _activeFormSection,
  patientFormNoScroll,
  onChangeFormValues,
  bookingTimeLabel,
  onSelectTime,
  setIsFormValuesDirty,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: MAX_WIDTH_MOBILE });
  const [searchParams] = useSearchParams();
  const cmsSiteId = searchParams.get('site') || searchParams.get('cmsSiteId') || '';

  const careProvideTimeSlotRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [scrollBarWideIndexes, setScrollBarWideIndexes] = useState<number[]>([]);

  const formOrder = getFormOrder(flow);

  const selectedDepartment = departmentOptions?.find((opt) => opt.value === formValues.departmentValue);
  const selectedDoctor = careProviderOptions?.find((opt) => opt.value === formValues.careProviderHid);
  const selectedServiceItem = useMemo(
    () =>
      serviceItemOptions && serviceItemOptions[0]
        ? 'serviceItem' in serviceItemOptions[0]
          ? (serviceItemOptions as ServiceItemsWithProviders[]).find(
              (si) => si.serviceItem.value === formValues.serviceItemHid,
            )
          : (serviceItemOptions as ServiceItem[]).find((si) => si.value === formValues.serviceItemHid)
        : undefined,
    [serviceItemOptions, formValues.serviceItemHid],
  );

  const onlyOneServiceItem = serviceItemOptions && serviceItemOptions.length > 0 && serviceItemOptions.length === 1;
  const onlyOneCareProvider = careProviderOptions && careProviderOptions.length > 0 && careProviderOptions.length === 1;

  const getSelectedLabels = (section: FORM_SECTION) => {
    switch (section) {
      case FORM_SECTION.DEPARTMENT:
        return [selectedDepartment?.name];
      case FORM_SECTION.DOCTOR:
        return [selectedDoctor?.name];
      case FORM_SECTION.SERVICE:
        return [
          selectedServiceItem
            ? getLocalizedValue({
                nameSc:
                  'serviceItem' in selectedServiceItem
                    ? selectedServiceItem.serviceItem.nameSc
                    : selectedServiceItem.nameSc,
                nameTc:
                  'serviceItem' in selectedServiceItem
                    ? selectedServiceItem.serviceItem.nameTc
                    : selectedServiceItem.nameTc,
                name:
                  'serviceItem' in selectedServiceItem
                    ? selectedServiceItem.serviceItem.name
                    : selectedServiceItem.name,
              })
            : undefined,
        ];
      case FORM_SECTION.DATE_TIME:
        return [bookingTimeLabel];
      case FORM_SECTION.PATIENT_FORM:
        return [
          formValues.lastName && formValues.firstName ? `${formValues.lastName} ${formValues.firstName}` : undefined,
          formValues.countryCode && formValues.mobileNo
            ? `(${formValues.countryCode}) ${formValues.mobileNo}`
            : undefined,
        ];
      default:
        return [];
    }
  };
  const getEditable = (section: FORM_SECTION) => {
    switch (section) {
      case FORM_SECTION.DEPARTMENT:
        return !!departmentOptions;
      case FORM_SECTION.DOCTOR:
        return !!formValues?.departmentValue && !!careProviderOptions && careProviderOptions.length > 1;
      case FORM_SECTION.SERVICE:
        return (
          (flow === FORM_FLOW.DEFAULT ? !!formValues?.careProviderHid : !!formValues?.departmentValue) &&
          !!serviceItemOptions &&
          serviceItemOptions.length > 1
        );
      case FORM_SECTION.DATE_TIME:
        return !!formValues?.serviceItemHid;
      case FORM_SECTION.PATIENT_FORM:
        return (
          !!formValues?.fromDt ||
          !!formValues?.lastName ||
          !!formValues?.firstName ||
          !!formValues?.mobileNo ||
          !!formValues?.email ||
          !!formValues?.isJoinedEhealth ||
          !!formValues?.remarks ||
          !!formValues?.idNo
        );
      default:
        return false;
    }
  };

  const [activeFormSection, setActiveFormSection] = useState<FORM_SECTION>(_activeFormSection);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<GetAvailableTimeSlotsRes | null>();
  const [availableTimeSlotsDetail, setAvailableTimeSlotsDetail] = useState<AvailableTimeslot[]>();
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [submitted, setSubmitted] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<GetAvailableTimeSlotsRes | null>();
  const [selectedTimeSlotsDetail, setSelectedTimeSlotsDetail] = useState<AvailableTimeslot[]>();
  const [availableCareProviderTimeSlotsDetail, setAvailableCareProviderTimeSlotsDetail] =
    useState<CareProviderAvailableTimeslot[]>();
  const [selectedCareProviderTimeSlotsDetail, setSelectedCareProviderTimeSlotsDetail] =
    useState<CareProviderAvailableTimeslot[]>();
  const [filterCareProvider, setFilterCareProvider] = useState('');
  const [selectedVideoUrl, setSelectedVideoUrl] = useState<string | null>(null);

  const handleVideoClick = (url: string) => {
    setSelectedVideoUrl(url);
  };

  const handleCloseVideo = () => {
    setSelectedVideoUrl(null);
  };

  const datePickerRef = useRef<ReactDatePicker>(null);

  const [minDate, setMinDate] = useState<Date>();
  const [maxDate, setMaxDate] = useState<Date>();

  const [confirmEditPopupSection, setConfirmEditPopupSection] = useState<FORM_SECTION>();
  const [confirmEditValue, setConfirmEditValue] = useState('');
  const [pendingSectionEdit, setPendingSectionEdit] = useState<FORM_SECTION>();
  const confirmedSectionEditRef = useRef<FORM_SECTION>();

  const hasRelatedSelections = (section: FORM_SECTION) => {
    switch (section) {
      case FORM_SECTION.DEPARTMENT:
        return !!(formValues.careProviderHid || formValues.serviceItemHid || formValues.fromDt || formValues.toDt);
      case FORM_SECTION.DOCTOR:
        return !!(formValues.serviceItemHid || formValues.fromDt || formValues.toDt);
      case FORM_SECTION.SERVICE:
        return !!(formValues.fromDt || formValues.toDt);
      default:
        return false;
    }
  };

  const handleSectionEdit = (section: FORM_SECTION) => {
    confirmedSectionEditRef.current = undefined;
    if (hasRelatedSelections(section)) {
      setPendingSectionEdit(section);
      return;
    }

    setActiveFormSection(section);
  };

  const [isCareProviderInfoPopupOpen, setCareProviderInfoPopupIsOpen] = useState(false);
  const [selectedCareProviderHid, setSelectedCareProviderHid] = useState<string>();

  const [patientFormErrors, setPatientFormErrors] = useState<PatientFormValues>({
    lastName: undefined,
    firstName: undefined,
    idNo: undefined,
    mobileNo: undefined,
    email: undefined,
    isJoinedEhealth: undefined,
    remarks: undefined,
  });

  const resetFormStates = () => {
    setSelectedDate(undefined);
    setSelectedTimeSlot(undefined);
    // setFilterCareProvider("");
    setAvailableTimeSlots(undefined);
    setAvailableTimeSlotsDetail(undefined);
    setAvailableCareProviderTimeSlotsDetail(undefined);
  };

  const onChangeDepartmentValue = (departmentValue: string, searchParam?: boolean) => {
    confirmedSectionEditRef.current = undefined;
    if (!searchParam) setIsFormValuesDirty(true);
    const newFlow = departmentOptions?.find((opt) => opt.value === departmentValue)?.flow;

    const department = departmentOptions?.find((opt) => opt.value === departmentValue);
    const careProvider = searchParam
      ? department?.careProviders?.find((cp) => cp.code === searchParams.get('cp'))
      : undefined;
    const careProviderSearchParamValue = careProvider?.value || undefined;
    const serviceItemSearchParamValue =
      newFlow === FORM_FLOW.DEFAULT
        ? careProvider?.serviceItems?.find((si) => si.value === searchParams.get('si'))?.value || undefined
        : getServiceItemsWithProviders(department)?.find((si) => si.serviceItem.value === searchParams.get('si'))
            ?.serviceItem?.value || undefined;

    const _careProviderOptions = department?.careProviders;
    const _onlyOneCareProvider = _careProviderOptions?.length === 1;

    const _serviceItemOptions =
      newFlow === FORM_FLOW.DEFAULT
        ? _onlyOneCareProvider
          ? department?.careProviders?.[0].serviceItems
          : department?.careProviders?.find((cp) => cp.value === careProviderSearchParamValue)?.serviceItems
        : getServiceItemsWithProviders(department);

    const _onlyOneServiceItem = _serviceItemOptions?.length === 1;

    resetFormStates();

    onSelectTime(undefined);
    onChangeFormValues({
      ...formValues,
      departmentValue,
      careProviderHid:
        newFlow === FORM_FLOW.DEFAULT && _onlyOneCareProvider
          ? _careProviderOptions[0].value
          : searchParam && careProviderSearchParamValue && newFlow === FORM_FLOW.DEFAULT
            ? careProviderSearchParamValue
            : undefined,
      serviceItemHid:
        (newFlow === FORM_FLOW.DEFAULT ? _onlyOneCareProvider : true) && _onlyOneServiceItem
          ? 'serviceItem' in _serviceItemOptions[0]
            ? _serviceItemOptions[0].serviceItem.value
            : _serviceItemOptions[0].value
          : serviceItemSearchParamValue
            ? serviceItemSearchParamValue
            : undefined,
      fromDt: undefined,
    });

    setFilterCareProvider(
      careProviderSearchParamValue &&
        newFlow === FORM_FLOW.DOCTOR_LAST &&
        (_serviceItemOptions?.length === 1 || serviceItemSearchParamValue)
        ? careProviderSearchParamValue
        : '',
    );

    setActiveFormSection(
      newFlow === FORM_FLOW.DEFAULT
        ? _onlyOneCareProvider
          ? _onlyOneServiceItem
            ? FORM_SECTION.DATE_TIME
            : FORM_SECTION.SERVICE
          : careProviderSearchParamValue
            ? serviceItemSearchParamValue
              ? FORM_SECTION.DATE_TIME
              : FORM_SECTION.SERVICE
            : FORM_SECTION.DOCTOR
        : _onlyOneServiceItem
          ? FORM_SECTION.DATE_TIME
          : serviceItemSearchParamValue
            ? FORM_SECTION.DATE_TIME
            : FORM_SECTION.SERVICE,
    );
  };

  const onChangeTimeSlot = (fromDt: string, toDt: string, careProviderHid?: string) => {
    setIsFormValuesDirty(true);
    const selectedDate = new Date(new Date(fromDt).setHours(0, 0, 0, 0));

    const displayingTimeSlotsDateKey = availableTimeSlots?.timeSlotDictionary
      ? Object.keys(availableTimeSlots.timeSlotDictionary)?.[0]
      : undefined;

    const displayingTimeSlotsDate = resetDateToMidnight(displayingTimeSlotsDateKey);

    if (displayingTimeSlotsDate && isSameMonthYear(displayingTimeSlotsDate, selectedDate))
      setSelectedTimeSlot(availableTimeSlots);
    else {
      const availableTimeSlotsDateKey = selectedTimeSlot?.timeSlotDictionary
        ? Object.keys(selectedTimeSlot.timeSlotDictionary)?.[0]
        : undefined;

      const availableTimeSlotsDate = resetDateToMidnight(availableTimeSlotsDateKey);

      if (!availableTimeSlotsDate || !isSameMonthYear(availableTimeSlotsDate, selectedDate)) {
        flow === FORM_FLOW.DOCTOR_LAST ? getTimeSlotsByCareProviders(selectedDate) : getTimeSlots(selectedDate);
      }
    }

    if (flow === FORM_FLOW.DOCTOR_LAST) {
      setSelectedCareProviderTimeSlotsDetail(availableCareProviderTimeSlotsDetail);
      const selectedAvailableCareProviderTimeSlots = availableCareProviderTimeSlotsDetail?.find(
        (careProviderTimeSlot) => careProviderTimeSlot.careProviderHid === careProviderHid,
      )?.availableTimeslots;
      onSelectTime(
        `${formatDate(fromDt!)} ${
          i18n.language === 'zh-CN'
            ? selectedAvailableCareProviderTimeSlots?.find((timeSlot) => timeSlot.fromDt === fromDt)?.labelSc
            : i18n.language === 'zh-TW' || i18n.language === 'zh-HK'
              ? selectedAvailableCareProviderTimeSlots?.find((timeSlot) => timeSlot.fromDt === fromDt)?.labelTc
              : selectedAvailableCareProviderTimeSlots?.find((timeSlot) => timeSlot.fromDt === fromDt)?.label
        } ${careProviderOptions?.find((careProvider) => careProvider.value === careProviderHid)?.name}`,
      );
    } else {
      setSelectedTimeSlotsDetail(availableTimeSlotsDetail);
      onSelectTime(
        `${formatDate(fromDt!)} ${
          i18n.language === 'zh-CN'
            ? availableTimeSlotsDetail?.find((timeSlot) => timeSlot.fromDt === fromDt)?.labelSc
            : i18n.language === 'zh-TW' || i18n.language === 'zh-HK'
              ? availableTimeSlotsDetail?.find((timeSlot) => timeSlot.fromDt === fromDt)?.labelTc
              : availableTimeSlotsDetail?.find((timeSlot) => timeSlot.fromDt === fromDt)?.label
        }`,
      );
    }
    onChangeFormValues({
      ...formValues,
      fromDt,
      toDt,
      careProviderHid: flow === FORM_FLOW.DOCTOR_LAST ? careProviderHid : formValues.careProviderHid,
    });
    setActiveFormSection(!isPatientFormRequiredFilled ? FORM_SECTION.PATIENT_FORM : FORM_SECTION.NULL);
  };

  useEffect(() => {
    if (!departmentOptions) return;
    const location = searchParams.get('location')
      ? departmentOptions?.find((opt) => opt.value === searchParams.get('location'))?.value
      : undefined;

    if (departmentOptions.length > 0 && !formValues.departmentValue && departmentOptions.length <= 1)
      onChangeDepartmentValue(departmentOptions[0]?.value, !!location);
    else if (location && !formValues.departmentValue) onChangeDepartmentValue(location, true);
  }, [departmentOptions, searchParams]);

  useEffect(() => {
    if (
      flow === FORM_FLOW.DEFAULT &&
      careProviderOptions &&
      careProviderOptions.length > 0 &&
      careProviderOptions.length <= 1 &&
      !formValues.careProviderHid
    )
      onChangeCareProviderHid(careProviderOptions[0]?.value);
  }, [careProviderOptions, formValues]);

  useEffect(() => {
    if (
      serviceItemOptions &&
      serviceItemOptions.length > 0 &&
      serviceItemOptions.length <= 1 &&
      !formValues.serviceItemHid
    )
      onChangeServiceItemHid(
        'serviceItem' in serviceItemOptions[0] ? serviceItemOptions[0].serviceItem.value : serviceItemOptions[0].value,
      );
  }, [serviceItemOptions, formValues]);

  const onChangeCareProviderHid = (careProviderHid: string) => {
    confirmedSectionEditRef.current = undefined;
    setIsFormValuesDirty(true);
    resetFormStates();
    onSelectTime(undefined);
    onChangeFormValues({
      ...formValues,
      careProviderHid,
      serviceItemHid:
        careProviderHid === formValues.careProviderHid &&
        serviceItemOptions &&
        serviceItemOptions.length > 0 &&
        serviceItemOptions.length === 1
          ? 'serviceItem' in serviceItemOptions[0]
            ? serviceItemOptions[0].serviceItem.value
            : serviceItemOptions[0].value
          : undefined,
      fromDt: undefined,
    });
    setActiveFormSection(
      careProviderHid === formValues.careProviderHid && serviceItemOptions && serviceItemOptions.length === 1
        ? FORM_SECTION.DATE_TIME
        : FORM_SECTION.SERVICE,
    );
  };

  const onChangeServiceItemHid = (serviceItemHid: string) => {
    confirmedSectionEditRef.current = undefined;
    setIsFormValuesDirty(true);
    resetFormStates();
    onSelectTime(undefined);
    onChangeFormValues({
      ...formValues,
      serviceItemHid,
      careProviderHid: flow === FORM_FLOW.DEFAULT ? formValues.careProviderHid : undefined,
      fromDt: undefined,
    });
    setActiveFormSection(FORM_SECTION.DATE_TIME);
  };

  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'firstName':
        return value ? validateName(value) : undefined;
      case 'lastName':
        return validateName(value);
      case 'idNo':
        return validateHKID(value);
      case 'mobileNo':
        return validatePhoneNumber(formValues.countryCode, value);
      case 'email':
        return !value ? undefined : validateEmail(value);
      case 'isJoinedEhealth':
        return validateEHealth(value);
      default:
        return undefined;
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | IOption) => {
    setIsFormValuesDirty(true);
    let name: string;
    let value: string;

    if ('target' in e) ({ name, value } = e.target);
    else ({ name, value } = e);

    onChangeFormValues({
      ...formValues,
      [name]: value,
    });

    if (submitted)
      setPatientFormErrors((prev) => ({
        ...prev,
        [name]: validateField(name, value),
      }));
  };

  const validateAllFields = () => {
    const errors = {
      lastName: validateName(formValues.lastName),
      firstName: formValues.firstName ? validateName(formValues.firstName) : undefined,
      idNo: validateHKID(formValues.idNo),
      mobileNo: validatePhoneNumber(formValues.countryCode, formValues.mobileNo),
      email: !formValues.email ? undefined : validateEmail(formValues.email),
      isJoinedEhealth: validateEHealth(formValues.isJoinedEhealth),
    };
    setPatientFormErrors(errors);

    if (Object.values(errors).some((error) => error !== undefined)) {
      activeFormSection !== FORM_SECTION.PATIENT_FORM && setActiveFormSection(FORM_SECTION.PATIENT_FORM);
      const firstErrorName = Object.entries(errors).find(([key, value]) => value !== undefined)?.[0] || null;
      const firstErrorElement = firstErrorName ? document.getElementsByName(firstErrorName)[0] : null;
      if (firstErrorElement) {
        setTimeout(
          () => {
            firstErrorElement.focus({ preventScroll: true });
            window.scrollTo({
              top: firstErrorElement.getBoundingClientRect().top + window.scrollY - 46,
              behavior: 'smooth',
            });
          },
          activeFormSection !== FORM_SECTION.PATIENT_FORM ? 750 : 0,
        );
      }
    }
    return !Object.values(errors).some((error) => error !== undefined);
  };

  const isPatientFormRequiredFilled =
    !!formValues.lastName &&
    !!formValues.firstName &&
    // !!formValues.idNo &&
    !!formValues.mobileNo &&
    !!formValues.isJoinedEhealth;

  const isAllRequiredFilled =
    !!formValues.serviceItemHid && !!formValues.careProviderHid && !!formValues.fromDt && !!formValues.toDt;

  const noTimeSlot =
    availableTimeSlots &&
    selectedServiceItem &&
    areAllTimeSlotsDisabledOrEmpty(availableTimeSlots.timeSlotDictionary) &&
    isSameMonthYear(new Date(availableTimeSlots.minDate), new Date(availableTimeSlots.maxDate));

  const getTimeSlots = (date?: Date, updateSelctedTimeSlot: boolean = true) => {
    const { serviceItemHid, careProviderHid } = formValues;
    if (!serviceItemHid || !careProviderHid) return Promise.resolve(null);

    const fromDt = moment(date).startOf('month').toDate();
    const toDt = moment(date).endOf('month').toDate();

    return new Promise<GetAvailableTimeSlotsRes>((resolve, reject) => {
      getAvailableTimeSlots(
        {
          serviceItemHid,
          careProviderHid,
          fromDt,
          toDt,
        },
        (res) => {
          setAvailableTimeSlots(res);
          if (updateSelctedTimeSlot) setSelectedTimeSlot(res);
          setMinDate(moment(res.minDate).toDate());
          setMaxDate(moment(res.maxDate).toDate());
          resolve(res);
        },
        (err) => {
          setAvailableTimeSlots(null);
          if (updateSelctedTimeSlot) setSelectedTimeSlot(null);
          setMinDate(undefined);
          setMaxDate(undefined);
          reject(err);
        },
      );
    });
  };

  const getTimeSlotsByCareProviders = (
    date?: Date,
    updateSelectedTimeSlot: boolean = true,
    careProviderHids?: string[],
  ) => {
    const { serviceItemHid } = formValues;
    if (!serviceItemHid) return Promise.resolve(null);

    const fromDt = moment(date).startOf('month').toDate();
    const toDt = moment(date).endOf('month').toDate();

    return new Promise<GetAvailableTimeSlotsRes>((resolve, reject) => {
      if (!selectedServiceItem || !('careProviders' in selectedServiceItem)) return;
      getAvailableTimeSlotsByCareProviders(
        {
          serviceItemHid,
          careProviderHids:
            careProviderHids || selectedServiceItem.careProviders.map((careProvider) => careProvider.value),
          fromDt,
          toDt,
        },
        (res) => {
          setAvailableTimeSlots(res);
          if (updateSelectedTimeSlot) setSelectedTimeSlot(res);
          setMinDate(moment(res.minDate).toDate());
          setMaxDate(moment(res.maxDate).toDate());
          resolve(res);
        },
        (err) => {
          setAvailableTimeSlots(null);
          if (updateSelectedTimeSlot) setSelectedTimeSlot(null);
          setMinDate(undefined);
          setMaxDate(undefined);
          reject(err);
        },
      );
    });
  };

  useEffect(() => {
    const availableTimeSlotsDetailDate = resetDateToMidnight(availableTimeSlotsDetail?.[0]?.fromDt);

    if (
      selectedDate &&
      availableTimeSlotsDetailDate &&
      moment(selectedDate).isSame(availableTimeSlotsDetailDate, 'day')
    )
      return;

    const { serviceItemHid, careProviderHid } = formValues;
    if (serviceItemHid && careProviderHid && selectedDate && flow === FORM_FLOW.DEFAULT) {
      getAvailableTimeSlotsDetail(
        {
          serviceItemHid,
          careProviderHid,
          fromDt: moment(selectedDate).toDate(),
        },
        (res) => setAvailableTimeSlotsDetail(res.availableTimeslots),
        () => setAvailableTimeSlotsDetail([]),
      );
      if (!availableTimeSlots) getTimeSlots(selectedDate);
    } else if (serviceItemHid && selectedDate && flow === FORM_FLOW.DOCTOR_LAST) {
      if (!selectedServiceItem || !('careProviders' in selectedServiceItem)) return;

      getAvailableTimeSlotsDetailByCareProviders(
        {
          serviceItemHid,
          careProviderHids:
            filterCareProvider === 'All' || filterCareProvider === ''
              ? selectedServiceItem.careProviders.map((careProvider) => careProvider.value)
              : [filterCareProvider],
          fromDt: moment(selectedDate).toDate(),
        },
        (res) => setAvailableCareProviderTimeSlotsDetail(res.careProviderAvailableTimeslots),
        () => setAvailableCareProviderTimeSlotsDetail([]),
      );

      if (!availableTimeSlots) getTimeSlotsByCareProviders(selectedDate);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (!selectedDate && formValues?.fromDt) setSelectedDate(resetDateToMidnight(formValues.fromDt));
  }, [formValues?.fromDt]);

  useEffect(() => {
    if (activeFormSection === FORM_SECTION.DATE_TIME && selectedServiceItem) {
      if (selectedTimeSlotsDetail && flow === FORM_FLOW.DEFAULT) setAvailableTimeSlotsDetail(selectedTimeSlotsDetail);
      if (selectedCareProviderTimeSlotsDetail && flow === FORM_FLOW.DOCTOR_LAST)
        setAvailableCareProviderTimeSlotsDetail(selectedCareProviderTimeSlotsDetail);
      setAvailableTimeSlots(selectedTimeSlot);

      const formDate = resetDateToMidnight(formValues.fromDt);

      const displayDate =
        formDate ||
        moment(
          'serviceItem' in selectedServiceItem ? selectedServiceItem.serviceItem.minDate : selectedServiceItem.minDate,
        ).toDate();

      setSelectedDate(formDate);

      const datePickerInstance = (datePickerRef.current as any)?.calendar?.instanceRef;
      if (datePickerInstance && displayDate) {
        !formDate && selectedTimeSlot && selectedTimeSlot.timeSlotDictionary
          ? datePickerInstance.changeMonthYear(Object.keys(selectedTimeSlot.timeSlotDictionary)[0])
          : datePickerInstance.changeMonthYear(displayDate);
      }

      const isSelectTimeslot = !!Object.keys(selectedTimeSlot?.timeSlotDictionary || {}).length;

      if (!isSelectTimeslot)
        (flow === FORM_FLOW.DOCTOR_LAST ? getTimeSlotsByCareProviders(displayDate) : getTimeSlots(displayDate)).then(
          (slots) => {
            if (slots && areAllTimeSlotsDisabledOrEmpty(slots.timeSlotDictionary)) {
              const slotMaxDate = new Date(slots.maxDate);
              if (
                datePickerInstance &&
                ((slotMaxDate.getFullYear() === displayDate.getFullYear() &&
                  slotMaxDate.getMonth() > displayDate.getMonth()) ||
                  slotMaxDate.getFullYear() > displayDate.getFullYear())
              ) {
                const nextMonthDate = moment(displayDate).add(1, 'month').startOf('month').toDate();
                datePickerInstance.changeMonthYear(nextMonthDate);
                flow === FORM_FLOW.DOCTOR_LAST
                  ? getTimeSlotsByCareProviders(nextMonthDate)
                  : getTimeSlots(nextMonthDate);
              }
            }
          },
        );
    }
  }, [activeFormSection, selectedServiceItem]);

  useEffect(() => {
    if (selectedServiceItem) {
      setMinDate(
        moment(
          'serviceItem' in selectedServiceItem ? selectedServiceItem.serviceItem.minDate : selectedServiceItem.minDate,
        ).toDate(),
      );
      setMaxDate(
        moment(
          'serviceItem' in selectedServiceItem ? selectedServiceItem.serviceItem.maxDate : selectedServiceItem.maxDate,
        ).toDate(),
      );
    }
  }, [formValues.serviceItemHid]);

  useDidUpdateEffect(() => {
    if (!selectedServiceItem || !('serviceItem' in selectedServiceItem)) return;

    setSelectedDate(undefined);
    setSelectedTimeSlot(undefined);
    setAvailableTimeSlots(undefined);
    setAvailableTimeSlotsDetail(undefined);
    setAvailableCareProviderTimeSlotsDetail(undefined);

    onChangeFormValues({
      ...formValues,
      careProviderHid: undefined,
      fromDt: undefined,
    });

    onSelectTime(undefined);

    const displayDate = moment(selectedServiceItem.serviceItem.minDate).toDate();
    const datePickerInstance = (datePickerRef.current as any)?.calendar?.instanceRef;

    datePickerInstance.changeMonthYear(displayDate);

    const careProviderHids =
      filterCareProvider === 'All' || filterCareProvider === ''
        ? selectedServiceItem.careProviders.map((careProvider) => careProvider.value)
        : [filterCareProvider];

    getTimeSlotsByCareProviders(displayDate, false, careProviderHids).then((slots) => {
      if (slots && areAllTimeSlotsDisabledOrEmpty(slots.timeSlotDictionary)) {
        const slotMaxDate = new Date(slots.maxDate);
        if (
          datePickerInstance &&
          ((slotMaxDate.getFullYear() === displayDate.getFullYear() &&
            slotMaxDate.getMonth() > displayDate.getMonth()) ||
            slotMaxDate.getFullYear() > displayDate.getFullYear())
        ) {
          const nextMonthDate = moment(displayDate).add(1, 'month').startOf('month').toDate();
          datePickerInstance.changeMonthYear(nextMonthDate);
          getTimeSlotsByCareProviders(nextMonthDate, false, careProviderHids);
        }
      }
    });
  }, [filterCareProvider]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const newWideIndexes: number[] = [];

      careProvideTimeSlotRefs.current.forEach((ele, index) => {
        if (!ele) return;

        const rect = ele.getBoundingClientRect();

        const boxRightEdge = rect.right;
        const boxTop = rect.top;
        const boxBottom = rect.bottom;

        const distanceX = boxRightEdge - e.clientX;
        const isNearRightEdge = distanceX < 12 && distanceX > 0;
        const isMouseInsideY = e.clientY >= boxTop && e.clientY <= boxBottom;

        if (isNearRightEdge && isMouseInsideY) newWideIndexes.push(index);
      });

      setScrollBarWideIndexes(newWideIndexes);
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <>
      <div className={`${styles.container} ${isMobile ? styles.mobile : ''}`}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <StepIndicator value={1} total={3} />
            <span className={styles.title}>{t('fillInInformation')}</span>
          </div>
          <Button
            type="secondary"
            onClick={() => navigate(`/ApptSysPublicWeb/v1/edit?site=${cmsSiteId}`)}
            className={styles.button}
          >
            {t('editOrCancelAppointment')}
          </Button>
        </div>
        <ControlledCollapsable isOpen={cmsSiteId.toUpperCase() === 'BH' && flow === FORM_FLOW.DOCTOR_LAST}>
          <div className={styles.hyperLinks}>
            <div
              className={styles.hyperLink}
              onClick={() => handleVideoClick('https://www.youtube.com/watch?v=rNLjZklkH8Y')}
            >
              <IconVideo />
              <a href="#" onClick={(e) => e.preventDefault()}>
                {t('webAppointmentGuidanceVideo')}
              </a>
            </div>
            {/* <div
            className={styles.hyperLink}
            onClick={() =>
              handleVideoClick("https://www.youtube.com/watch?v=o3pk4PmupPI")
            }
          >
            <IconVideo />
            <a href="#" onClick={(e) => e.preventDefault()}>
              {t("appAppointmentGuidanceVideo")}
            </a>
          </div>
          <div className={styles.hyperLink}>
            <IconDownloadApp />
            <a href="#" target="_blank" rel="noopener noreferrer">
              {t("downloadApp")}
            </a>
          </div> */}
          </div>
        </ControlledCollapsable>
        <VideoPopup isOpen={!!selectedVideoUrl} videoUrl={selectedVideoUrl || ''} onClose={handleCloseVideo} />

        <div className={styles.content}>
          <div className={styles.blocks}>
            {(flow === FORM_FLOW.DOCTOR_LAST
              ? formOrder.filter((section) => !(onlyOneServiceItem && section === FORM_SECTION.SERVICE))
              : formOrder
            ).map((section, index) => {
              const title =
                formValues.departmentValue || index === 0 ? `${index + 1}. ${FORM_SECTION_LABELS[section]}` : '';
              const noScroll =
                section === FORM_SECTION.DEPARTMENT
                  ? !formValues.departmentValue
                  : section === FORM_SECTION.PATIENT_FORM
                    ? patientFormNoScroll || Object.values(patientFormErrors).some((error) => error !== undefined)
                    : section === FORM_SECTION.SERVICE
                      ? flow === FORM_FLOW.DOCTOR_LAST
                      : section === FORM_SECTION.DATE_TIME
                        ? !!formValues.serviceItemHid && flow === FORM_FLOW.DOCTOR_LAST
                        : false;

              return (
                <CollapsableBlock
                  key={section}
                  title={title}
                  selectedLabels={getSelectedLabels(section)}
                  editable={getEditable(section)}
                  active={activeFormSection === section}
                  onEdit={() => handleSectionEdit(section)}
                  noScroll={noScroll}
                >
                  {section === FORM_SECTION.DEPARTMENT ? (
                    (departmentOptions || departmentOptions === null) && (
                      <div className={styles.serviceBlockContentContainer}>
                        {departmentOptions.length === 0 || departmentOptions === null ? (
                          <span className={styles.noOptions}>{t('noOptions')}</span>
                        ) : (
                          <VerticalSelector
                            onClick={(departmentValue) => {
                              const newFlow = departmentOptions?.find(
                                (opt) => opt.value === formValues.departmentValue,
                              )?.flow;
                              if (confirmedSectionEditRef.current === FORM_SECTION.DEPARTMENT) {
                                onChangeDepartmentValue(departmentValue);
                              } else if (
                                (newFlow === FORM_FLOW.DEFAULT && formValues.careProviderHid) ||
                                (flow === FORM_FLOW.DOCTOR_LAST && newFlow === FORM_FLOW.DEFAULT)
                              ) {
                                setConfirmEditValue(departmentValue);
                                setConfirmEditPopupSection(FORM_SECTION.DEPARTMENT);
                              } else if (
                                (newFlow === FORM_FLOW.DOCTOR_LAST && formValues.serviceItemHid) ||
                                (flow === FORM_FLOW.DEFAULT && newFlow === FORM_FLOW.DOCTOR_LAST)
                              ) {
                                setConfirmEditValue(departmentValue);
                                setConfirmEditPopupSection(FORM_SECTION.DEPARTMENT);
                              } else onChangeDepartmentValue(departmentValue);
                            }}
                            options={departmentOptions.map((opt) => ({
                              name: opt.name,
                              value: opt.value,
                            }))}
                            selectedValue={formValues.departmentValue}
                          />
                        )}
                        <div className={styles.appointmentMessage}>
                          {t('appointmentMessage')}
                          <div className={styles.hospitalLink}>
                            <Trans i18nKey={cmsSiteId.toUpperCase() === 'EKMC' ? 'ekmcHospitalLink' : 'hospitalLink'}>
                              <Link
                                to={`${
                                  cmsSiteId.toUpperCase() === 'EKMC'
                                    ? `https://www.hkbh.org.hk${
                                        i18n.language === 'zh-HK'
                                          ? ''
                                          : i18n.language === 'zh-CN'
                                            ? '/?lang=zh-hans'
                                            : '/?lang=en'
                                      }`
                                    : process.env.EKMC_URL ||
                                      `https://www.hkbh.org.hk/EKMC/${
                                        i18n.language === 'zh-HK' ? 'zh' : i18n.language === 'zh-CN' ? 'cn' : 'en'
                                      }/`
                                }`}
                              >
                                {cmsSiteId.toUpperCase() === 'EKMC'
                                  ? 'HKBH East Kowloon Medical Centre'
                                  : 'Hong Kong Baptist Hospital'}
                              </Link>
                              or
                              <Link to={getAmcOnlineBookingUrl(i18n.language)}>HKBH Ambulatory Medical Centre</Link>
                            </Trans>
                          </div>
                        </div>
                      </div>
                    )
                  ) : section === FORM_SECTION.DOCTOR ? (
                    (careProviderOptions || careProviderOptions === null) && (
                      <>
                        {careProviderOptions.length === 0 || careProviderOptions === null ? (
                          <span className={styles.noOptions}>{t('noOptions')}</span>
                        ) : (
                          <VerticalSelector
                            onClick={(careProviderHid) => {
                              if (
                                confirmedSectionEditRef.current !== FORM_SECTION.DOCTOR &&
                                formValues.serviceItemHid
                              ) {
                                setConfirmEditValue(careProviderHid);
                                setConfirmEditPopupSection(FORM_SECTION.DOCTOR);
                              } else onChangeCareProviderHid(careProviderHid);
                            }}
                            options={careProviderOptions.map((opt) => ({
                              name: opt.name,
                              value: opt.value,
                            }))}
                            selectedValue={formValues.careProviderHid}
                            infoOnClick={(hid) => {
                              setSelectedCareProviderHid(hid);
                              setCareProviderInfoPopupIsOpen(true);
                            }}
                          />
                        )}
                      </>
                    )
                  ) : section === FORM_SECTION.SERVICE ? (
                    (serviceItemOptions || serviceItemOptions === null) && (
                      <>
                        {serviceItemOptions.length === 0 || serviceItemOptions === null ? (
                          <span className={styles.noOptions}>{t('noOptions')}</span>
                        ) : (
                          <VerticalSelector
                            onClick={(serviceItemHid) => {
                              if (
                                confirmedSectionEditRef.current !== FORM_SECTION.SERVICE &&
                                formValues.fromDt &&
                                formValues.toDt
                              ) {
                                setConfirmEditValue(serviceItemHid);
                                setConfirmEditPopupSection(FORM_SECTION.SERVICE);
                              } else {
                                onChangeServiceItemHid(serviceItemHid);
                                setFilterCareProvider('');
                              }
                            }}
                            options={serviceItemOptions.map((opt) => ({
                              name:
                                getLocalizedValue({
                                  nameSc: 'serviceItem' in opt ? opt.serviceItem.nameSc : opt.nameSc,
                                  nameTc: 'serviceItem' in opt ? opt.serviceItem.nameTc : opt.nameTc,
                                  name: 'serviceItem' in opt ? opt.serviceItem.name : opt.name,
                                }) || '',
                              value: 'serviceItem' in opt ? opt.serviceItem.value : opt.value,
                            }))}
                            selectedValue={formValues.serviceItemHid}
                          />
                        )}
                      </>
                    )
                  ) : section === FORM_SECTION.DATE_TIME ? (
                    <div className={styles.dateTimeContainer}>
                      {isMobile &&
                        flow === FORM_FLOW.DOCTOR_LAST &&
                        selectedServiceItem &&
                        'careProviders' in selectedServiceItem && (
                          <Dropdown
                            clearable
                            placeholder={t('doctorOrCareProvider')}
                            options={[
                              {
                                name: t('allDoctorsOrCareProviders'),
                                value: 'All',
                              },
                              ...selectedServiceItem.careProviders,
                            ]}
                            value={filterCareProvider}
                            onChange={(careProvider) => setFilterCareProvider(careProvider)}
                            style={
                              !availableTimeSlots
                                ? {
                                    pointerEvents: 'none',
                                    opacity: 0.3,
                                  }
                                : {}
                            }
                            infoOnClick={(hid) => {
                              setSelectedCareProviderHid(hid);
                              setCareProviderInfoPopupIsOpen(true);
                            }}
                          />
                        )}
                      <div className={styles.legends}>
                        <div className={styles.legend}>
                          <div className={`${styles.icon} ${styles.amAvaliable}`}></div>
                          <span>{t('amAvailable')}</span>
                        </div>
                        <div className={styles.legend}>
                          <div className={`${styles.icon} ${styles.pmAvaliable}`}></div>
                          <span>{t('pmAvailable')}</span>
                        </div>
                        <div className={styles.legend}>
                          <div className={`${styles.icon} ${styles.amPmAvaliable}`}></div>
                          <span>{t('amPmAvailable')}</span>
                        </div>
                        <div className={styles.legend}>
                          <div className={`${styles.icon} ${styles.selectedDate}`}></div>
                          <span>{t('selectedDate')}</span>
                        </div>
                      </div>
                      <div
                        className={`${styles.dateTimePicker} ${
                          flow === FORM_FLOW.DOCTOR_LAST ? styles.flowDoctorLast : ''
                        }`}
                      >
                        <div className={styles.datePickerWithReminder}>
                          <div className={styles.datePickerNoTimeSlotContainer}>
                            {noTimeSlot ? (
                              <div className={styles.noTimeSlot}>{t('noTimeSlot')}</div>
                            ) : (
                              <div
                                className={styles.datePickerContainer}
                                style={
                                  (selectedServiceItem &&
                                    'careProviders' in selectedServiceItem &&
                                    selectedDate &&
                                    !availableCareProviderTimeSlotsDetail) ||
                                  (selectedServiceItem &&
                                    !('careProviders' in selectedServiceItem) &&
                                    selectedDate &&
                                    !availableTimeSlotsDetail)
                                    ? { opacity: 0.3, pointerEvents: 'none' }
                                    : { opacity: 1 }
                                }
                              >
                                <div className={styles.datePicker}>
                                  <DatePicker
                                    datePickerRef={datePickerRef}
                                    date={selectedDate}
                                    onMonthChange={(date) => {
                                      setAvailableTimeSlots(undefined);
                                      flow === FORM_FLOW.DOCTOR_LAST
                                        ? getTimeSlotsByCareProviders(
                                            date,
                                            false,
                                            filterCareProvider !== 'All' && filterCareProvider !== ''
                                              ? [filterCareProvider]
                                              : undefined,
                                          )
                                        : getTimeSlots(date, false);
                                    }}
                                    onChange={(date) => {
                                      flow === FORM_FLOW.DOCTOR_LAST
                                        ? setAvailableCareProviderTimeSlotsDetail(undefined)
                                        : setAvailableTimeSlotsDetail(undefined);
                                      const timeSlotDate = Object.keys(
                                        availableTimeSlots?.timeSlotDictionary || {},
                                      ).find((dt) => moment(tzStartDay(dt)).isSame(date, 'date'));
                                      if (timeSlotDate) {
                                        setSelectedDate(moment(timeSlotDate).toDate());
                                      }
                                    }}
                                    filterDate={!minDate || !maxDate ? () => false : undefined}
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    excludedDates={
                                      availableTimeSlots?.timeSlotDictionary
                                        ? Object.keys(availableTimeSlots.timeSlotDictionary).length > 0
                                          ? Object.keys(availableTimeSlots.timeSlotDictionary)
                                              .filter(
                                                (key) =>
                                                  availableTimeSlots.timeSlotDictionary![key].isDisabled ||
                                                  (availableTimeSlots.timeSlotDictionary![key].subGroups[0].roomSlot ===
                                                    0 &&
                                                    availableTimeSlots.timeSlotDictionary![key].subGroups[1]
                                                      .roomSlot === 0),
                                              )
                                              .map((date) => new Date(date))
                                          : []
                                        : []
                                    }
                                    dayClassName={(date) => {
                                      const timeSlotDate = Object.keys(
                                        availableTimeSlots?.timeSlotDictionary || {},
                                      ).find((dt) => moment(tzStartDay(dt)).isSame(date, 'date'));
                                      if (availableTimeSlots?.timeSlotDictionary && timeSlotDate) {
                                        const day = availableTimeSlots.timeSlotDictionary[timeSlotDate];

                                        const amGroup = day.subGroups.find((group) => group.name === 'AM');
                                        const pmGroup = day.subGroups.find((group) => group.name === 'PM');
                                        const amAvailable = amGroup && (amGroup.fulfillSlot || amGroup.roomSlot);
                                        const pmAvailable = pmGroup && (pmGroup.fulfillSlot || pmGroup.roomSlot);

                                        if (amAvailable && pmAvailable) return 'full';
                                        else if (amAvailable) return 'am';
                                        else if (pmAvailable) return 'pm';
                                        else return 'react-datepicker__day--disabled';
                                      }
                                      return 'react-datepicker__day--disabled';
                                    }}
                                    isLoading={availableTimeSlots === undefined}
                                    disable={availableTimeSlots === null}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                          {selectedDepartment?.notes && (
                            <div className={styles.reminder}>
                              <IconReminder />
                              <span>{selectedDepartment.notes}</span>
                            </div>
                          )}
                        </div>
                        {!noTimeSlot &&
                          (flow === FORM_FLOW.DEFAULT ? (
                            <div className={styles.timePicker}>
                              <TimePicker
                                selectedDate={selectedDate}
                                selectedTime={formValues?.fromDt}
                                availableTimeslots={availableTimeSlotsDetail}
                                onSelect={onChangeTimeSlot}
                              />
                            </div>
                          ) : flow === FORM_FLOW.DOCTOR_LAST ? (
                            isMobile ? (
                              <>
                                {selectedServiceItem && 'careProviders' in selectedServiceItem && selectedDate && (
                                  <div className={styles.careProviderTimeSlotsMobile}>
                                    {availableCareProviderTimeSlotsDetail ||
                                    (filterCareProvider && filterCareProvider !== 'All') ? (
                                      availableCareProviderTimeSlotsDetail &&
                                      availableCareProviderTimeSlotsDetail.filter(
                                        (careProviderTimeslot) => careProviderTimeslot.availableTimeslots.length > 0,
                                      ).length > 1 &&
                                      (!filterCareProvider || filterCareProvider === 'All') ? (
                                        availableCareProviderTimeSlotsDetail
                                          .filter(
                                            (careProviderTimeslot) =>
                                              careProviderTimeslot.availableTimeslots.length > 0,
                                          )
                                          .map((careProviderTimeSlot) => (
                                            <CollapsableElement
                                              key={careProviderTimeSlot.careProviderHid}
                                              className={styles.collapsableElementContainer}
                                              titleRenderer={(isExpanded) => (
                                                <div className={styles.title}>
                                                  <div className={styles.leftSide}>
                                                    <span className={styles.careProviderName}>
                                                      {
                                                        selectedServiceItem.careProviders.find(
                                                          (opt) => opt.value === careProviderTimeSlot.careProviderHid,
                                                        )?.name
                                                      }
                                                    </span>
                                                    <div className={styles.text}>
                                                      <span>{`${t('earliestAvailableTimeSlot')}: ${
                                                        i18n.language === 'zh-CN'
                                                          ? careProviderTimeSlot.availableTimeslots[0].labelSc
                                                          : i18n.language === 'zh-TW' || i18n.language === 'zh-HK'
                                                            ? careProviderTimeSlot.availableTimeslots[0].labelTc
                                                            : careProviderTimeSlot.availableTimeslots[0].label
                                                      }`}</span>
                                                    </div>
                                                  </div>

                                                  <div className={styles.rightSide}>
                                                    <img
                                                      src={IconInfo}
                                                      width={25}
                                                      height={25}
                                                      alt="info"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedCareProviderHid(
                                                          careProviderTimeSlot.careProviderHid,
                                                        );
                                                        setCareProviderInfoPopupIsOpen(true);
                                                      }}
                                                    />
                                                    <IconExpand
                                                      className={styles.expandIcon}
                                                      style={{
                                                        transform: isExpanded ? 'rotate(180deg)' : '',
                                                      }}
                                                    />
                                                  </div>
                                                </div>
                                              )}
                                            >
                                              <div className={styles.timeSlotMobile}>
                                                {careProviderTimeSlot.availableTimeslots.map((timeSlot, index) => {
                                                  return (
                                                    <button
                                                      className={styles.timeSlotLabelMobile}
                                                      key={index}
                                                      onClick={() => {
                                                        onChangeTimeSlot(
                                                          timeSlot.fromDt,
                                                          timeSlot.toDt,
                                                          careProviderTimeSlot.careProviderHid,
                                                        );
                                                      }}
                                                    >
                                                      {i18n.language === 'zh-CN'
                                                        ? timeSlot.labelSc
                                                        : i18n.language === 'zh-TW' || i18n.language === 'zh-HK'
                                                          ? timeSlot.labelTc
                                                          : timeSlot.label}
                                                    </button>
                                                  );
                                                })}
                                              </div>
                                            </CollapsableElement>
                                          ))
                                      ) : availableCareProviderTimeSlotsDetail &&
                                        availableCareProviderTimeSlotsDetail.filter(
                                          (careProviderTimeslot) => careProviderTimeslot.availableTimeslots.length > 0,
                                        ).length === 0 ? (
                                        <div className={styles.noTimeSlot}>{t('noTimeSlot')}</div>
                                      ) : (
                                        <div
                                          className={`${styles.timePicker} ${styles.timePickerWithCareProviderNameMobile}`}
                                        >
                                          <div className={styles.careProviderNameContainer}>
                                            <span className={styles.careProviderName}>
                                              {selectedServiceItem.careProviders.find(
                                                (opt) => opt.value === filterCareProvider,
                                              )?.name ||
                                                selectedServiceItem.careProviders.find(
                                                  (opt) =>
                                                    opt.value ===
                                                    availableCareProviderTimeSlotsDetail?.filter(
                                                      (careProviderTimeslot) =>
                                                        careProviderTimeslot.availableTimeslots.length > 0,
                                                    )[0].careProviderHid,
                                                )?.name}
                                            </span>
                                            <img
                                              src={IconInfo}
                                              width={25}
                                              height={25}
                                              alt="info"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedCareProviderHid(
                                                  filterCareProvider ||
                                                    availableCareProviderTimeSlotsDetail?.filter(
                                                      (careProviderTimeslot) =>
                                                        careProviderTimeslot.availableTimeslots.length > 0,
                                                    )[0].careProviderHid,
                                                );
                                                setCareProviderInfoPopupIsOpen(true);
                                              }}
                                            />
                                          </div>
                                          <TimePicker
                                            selectedDate={selectedDate}
                                            selectedTime={formValues?.fromDt}
                                            availableTimeslots={
                                              availableCareProviderTimeSlotsDetail?.filter(
                                                (careProviderTimeslot) =>
                                                  careProviderTimeslot.availableTimeslots.length > 0,
                                              )[0].availableTimeslots
                                            }
                                            onSelect={(formDt, toDt) =>
                                              onChangeTimeSlot(
                                                formDt,
                                                toDt,
                                                filterCareProvider && filterCareProvider !== 'All'
                                                  ? filterCareProvider
                                                  : availableCareProviderTimeSlotsDetail?.filter(
                                                      (careProviderTimeslot) =>
                                                        careProviderTimeslot.availableTimeslots.length > 0,
                                                    )[0].careProviderHid,
                                              )
                                            }
                                          />
                                        </div>
                                      )
                                    ) : (
                                      <div className={styles.careProviderTimeSlotsMobileSkeleton}>
                                        <div className={styles.skeletonContainer}>
                                          <Skeleton width={84} height={20} />
                                          <Skeleton width={207} height={16} />
                                          <div className={styles.bottomBorder}></div>
                                        </div>
                                        <div className={styles.skeletonContainer}>
                                          <Skeleton width={70} height={20} />
                                          <Skeleton width={207} height={16} />
                                          <div className={styles.bottomBorder}></div>
                                        </div>
                                        <div className={styles.skeletonContainer}>
                                          <Skeleton width={84} height={20} />
                                          <Skeleton width={207} height={16} />
                                          <div className={styles.bottomBorder}></div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                <div
                                  className={`${styles.careProviderDropdown} ${!availableTimeSlots && styles.disable}`}
                                >
                                  {selectedServiceItem && 'careProviders' in selectedServiceItem && (
                                    <Dropdown
                                      style={{ zIndex: 0 }}
                                      clearable
                                      type="blocks"
                                      placeholder={t('doctorOrCareProvider')}
                                      options={[
                                        {
                                          name: t('allDoctorsOrCareProviders'),
                                          value: 'All',
                                        },
                                        ...selectedServiceItem.careProviders,
                                      ]}
                                      value={filterCareProvider}
                                      onChange={(careProvider) => setFilterCareProvider(careProvider)}
                                      infoOnClick={(hid) => {
                                        setSelectedCareProviderHid(hid);
                                        setCareProviderInfoPopupIsOpen(true);
                                      }}
                                    />
                                  )}
                                </div>

                                {selectedServiceItem && 'careProviders' in selectedServiceItem && selectedDate && (
                                  <div className={styles.careProviderTimeSlots}>
                                    {availableCareProviderTimeSlotsDetail ||
                                    (filterCareProvider && filterCareProvider !== 'All') ? (
                                      availableCareProviderTimeSlotsDetail &&
                                      availableCareProviderTimeSlotsDetail.filter(
                                        (careProviderTimeslot) => careProviderTimeslot.availableTimeslots.length > 0,
                                      ).length > 1 &&
                                      (!filterCareProvider || filterCareProvider === 'All') ? (
                                        (() => {
                                          const items = availableCareProviderTimeSlotsDetail.filter(
                                            (careProviderTimeslot) =>
                                              careProviderTimeslot.availableTimeslots.length > 0,
                                          );
                                          const length = items.length;
                                          const split = splitMap[length as 6 | 7 | 8 | 9];

                                          return split ? (
                                            <>
                                              <div className={styles.careProviderTimeSlots}>
                                                {items.slice(0, split[0]).map((item, index) => (
                                                  <div key={index} className={styles.careProviderTimeSlot}>
                                                    <div className={styles.careProviderName}>
                                                      <span>
                                                        {
                                                          selectedServiceItem.careProviders.find(
                                                            (opt) => opt.value === item.careProviderHid,
                                                          )?.name
                                                        }
                                                      </span>
                                                      <img
                                                        src={IconInfo}
                                                        width={25}
                                                        height={25}
                                                        alt="info"
                                                        onClick={() => {
                                                          setSelectedCareProviderHid(item.careProviderHid);
                                                          setCareProviderInfoPopupIsOpen(true);
                                                        }}
                                                      />
                                                    </div>

                                                    <div
                                                      ref={(el) => {
                                                        careProvideTimeSlotRefs.current[index] = el;
                                                      }}
                                                      className={`${styles.timeSlot} ${
                                                        scrollBarWideIndexes.includes(index) ? styles.moreWidth : ''
                                                      }`}
                                                    >
                                                      {item.availableTimeslots.map((timeSlot, timeSlotIndex) => (
                                                        <button
                                                          className={styles.timeSlotLabel}
                                                          key={timeSlotIndex}
                                                          onClick={() => {
                                                            onChangeTimeSlot(
                                                              timeSlot.fromDt,
                                                              timeSlot.toDt,
                                                              item.careProviderHid,
                                                            );
                                                          }}
                                                        >
                                                          <span>
                                                            {i18n.language === 'zh-CN'
                                                              ? timeSlot.labelSc
                                                              : i18n.language === 'zh-TW' || i18n.language === 'zh-HK'
                                                                ? timeSlot.labelTc
                                                                : timeSlot.label}
                                                          </span>
                                                        </button>
                                                      ))}
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                              <div className={styles.careProviderTimeSlots}>
                                                {items.slice(split[0], split[0] + split[1]).map((item, index) => (
                                                  <div key={index} className={styles.careProviderTimeSlot}>
                                                    <div className={styles.careProviderName}>
                                                      <span>
                                                        {
                                                          selectedServiceItem.careProviders.find(
                                                            (opt) => opt.value === item.careProviderHid,
                                                          )?.name
                                                        }
                                                      </span>
                                                      <img
                                                        src={IconInfo}
                                                        width={25}
                                                        height={25}
                                                        alt="info"
                                                        onClick={() => {
                                                          setSelectedCareProviderHid(item.careProviderHid);
                                                          setCareProviderInfoPopupIsOpen(true);
                                                        }}
                                                      />
                                                    </div>

                                                    <div
                                                      ref={(el) => {
                                                        careProvideTimeSlotRefs.current[index] = el;
                                                      }}
                                                      className={`${styles.timeSlot} ${
                                                        scrollBarWideIndexes.includes(index) ? styles.moreWidth : ''
                                                      }`}
                                                    >
                                                      {item.availableTimeslots.map((timeSlot, timeSlotIndex) => (
                                                        <button
                                                          className={styles.timeSlotLabel}
                                                          key={timeSlotIndex}
                                                          onClick={() => {
                                                            onChangeTimeSlot(
                                                              timeSlot.fromDt,
                                                              timeSlot.toDt,
                                                              item.careProviderHid,
                                                            );
                                                          }}
                                                        >
                                                          <span>
                                                            {i18n.language === 'zh-CN'
                                                              ? timeSlot.labelSc
                                                              : i18n.language === 'zh-TW' || i18n.language === 'zh-HK'
                                                                ? timeSlot.labelTc
                                                                : timeSlot.label}
                                                          </span>
                                                        </button>
                                                      ))}
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            </>
                                          ) : (
                                            availableCareProviderTimeSlotsDetail
                                              .filter(
                                                (careProviderTimeslot) =>
                                                  careProviderTimeslot.availableTimeslots.length > 0,
                                              )
                                              .map((careProviderTimeSlot, index) => (
                                                <div key={index} className={styles.careProviderTimeSlot}>
                                                  <div className={styles.careProviderName}>
                                                    <span>
                                                      {
                                                        selectedServiceItem.careProviders.find(
                                                          (opt) => opt.value === careProviderTimeSlot.careProviderHid,
                                                        )?.name
                                                      }
                                                    </span>
                                                    <img
                                                      src={IconInfo}
                                                      width={25}
                                                      height={25}
                                                      alt="info"
                                                      onClick={() => {
                                                        setSelectedCareProviderHid(
                                                          careProviderTimeSlot.careProviderHid,
                                                        );
                                                        setCareProviderInfoPopupIsOpen(true);
                                                      }}
                                                    />
                                                  </div>

                                                  <div
                                                    ref={(el) => {
                                                      careProvideTimeSlotRefs.current[index] = el;
                                                    }}
                                                    className={`${styles.timeSlot} ${
                                                      scrollBarWideIndexes.includes(index) ? styles.moreWidth : ''
                                                    }`}
                                                  >
                                                    {careProviderTimeSlot.availableTimeslots.map(
                                                      (timeSlot, timeSlotIndex) => (
                                                        <button
                                                          className={styles.timeSlotLabel}
                                                          key={timeSlotIndex}
                                                          onClick={() => {
                                                            onChangeTimeSlot(
                                                              timeSlot.fromDt,
                                                              timeSlot.toDt,
                                                              careProviderTimeSlot.careProviderHid,
                                                            );
                                                          }}
                                                        >
                                                          <span>
                                                            {i18n.language === 'zh-CN'
                                                              ? timeSlot.labelSc
                                                              : i18n.language === 'zh-TW' || i18n.language === 'zh-HK'
                                                                ? timeSlot.labelTc
                                                                : timeSlot.label}
                                                          </span>
                                                        </button>
                                                      ),
                                                    )}
                                                  </div>
                                                </div>
                                              ))
                                          );
                                        })()
                                      ) : availableCareProviderTimeSlotsDetail &&
                                        availableCareProviderTimeSlotsDetail.filter(
                                          (careProviderTimeslot) => careProviderTimeslot.availableTimeslots.length > 0,
                                        ).length === 0 ? (
                                        <div className={styles.noTimeSlot}>{t('noTimeSlot')}</div>
                                      ) : (
                                        <div
                                          className={`${styles.timePicker} ${styles.timePickerWithCareProviderName}`}
                                        >
                                          <div className={styles.careProviderNameContainer}>
                                            <span className={styles.careProviderName}>
                                              {selectedServiceItem.careProviders.find(
                                                (opt) => opt.value === filterCareProvider,
                                              )?.name ||
                                                selectedServiceItem.careProviders.find(
                                                  (opt) =>
                                                    opt.value ===
                                                    availableCareProviderTimeSlotsDetail?.filter(
                                                      (careProviderTimeslot) =>
                                                        careProviderTimeslot.availableTimeslots.length > 0,
                                                    )?.[0].careProviderHid,
                                                )?.name}
                                            </span>
                                            <img
                                              src={IconInfo}
                                              width={25}
                                              height={25}
                                              alt="info"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedCareProviderHid(
                                                  filterCareProvider ||
                                                    availableCareProviderTimeSlotsDetail?.filter(
                                                      (careProviderTimeslot) =>
                                                        careProviderTimeslot.availableTimeslots.length > 0,
                                                    )[0].careProviderHid,
                                                );
                                                setCareProviderInfoPopupIsOpen(true);
                                              }}
                                            />
                                          </div>
                                          <TimePicker
                                            selectedDate={selectedDate}
                                            selectedTime={formValues?.fromDt}
                                            availableTimeslots={
                                              availableCareProviderTimeSlotsDetail?.filter(
                                                (careProviderTimeslot) =>
                                                  careProviderTimeslot.availableTimeslots.length > 0,
                                              )[0].availableTimeslots
                                            }
                                            onSelect={(formDt, toDt) =>
                                              onChangeTimeSlot(
                                                formDt,
                                                toDt,
                                                filterCareProvider && filterCareProvider !== 'All'
                                                  ? filterCareProvider
                                                  : availableCareProviderTimeSlotsDetail?.filter(
                                                      (careProviderTimeslot) =>
                                                        careProviderTimeslot.availableTimeslots.length > 0,
                                                    )[0].careProviderHid,
                                              )
                                            }
                                            twoColumns
                                          />
                                        </div>
                                      )
                                    ) : (
                                      <>
                                        <div className={styles.careProviderTimeSlot}>
                                          <div className={styles.careProviderName}>
                                            <Skeleton width={96} height={24} />
                                          </div>
                                          <div className={`${styles.timeSlot} ${styles.skeleton}`}>
                                            <Skeleton width="100%" height={20} />
                                            <Skeleton width="100%" height={20} />
                                            <Skeleton width="100%" height={20} />
                                          </div>
                                        </div>
                                        <div className={styles.careProviderTimeSlot}>
                                          <div className={styles.careProviderName}>
                                            <Skeleton width={80} height={24} />
                                          </div>
                                          <div className={`${styles.timeSlot} ${styles.skeleton}`}>
                                            <Skeleton width="100%" height={20} />
                                            <Skeleton width="100%" height={20} />
                                          </div>
                                        </div>
                                        <div className={styles.careProviderTimeSlot}>
                                          <div className={styles.careProviderName}>
                                            <Skeleton width={96} height={24} />
                                          </div>
                                          <div className={`${styles.timeSlot} ${styles.skeleton}`}>
                                            <Skeleton width="100%" height={20} />
                                            <Skeleton width="100%" height={20} />
                                            <Skeleton width="100%" height={20} />
                                            <Skeleton width="100%" height={20} />
                                            <Skeleton width="100%" height={20} />
                                          </div>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                )}
                              </>
                            )
                          ) : null)}
                      </div>
                    </div>
                  ) : section === FORM_SECTION.PATIENT_FORM ? (
                    <PatientForm errors={patientFormErrors} formValues={formValues} onChange={handleChange} />
                  ) : null}
                </CollapsableBlock>
              );
            })}
          </div>
          <div className={styles.button}>
            <Button
              type="primary"
              disabled={!isAllRequiredFilled}
              onClick={() => {
                setSubmitted(true);
                if (validateAllFields()) onSubmit(availableTimeSlotsDetail!);
                // else {
                //   setActiveFormSection(FORM_SECTION.PATIENT_FORM);
                // }
              }}
            >
              <span>{t('nextStepPreview')}</span>
            </Button>
          </div>
        </div>
      </div>
      <Popup
        title={t('confirmEdit')}
        isOpen={!!pendingSectionEdit || (!!confirmEditValue && !!confirmEditPopupSection)}
        content={t('confirmEditDetail')}
        status={POPUP_STATUS.WARNING}
        confirmTitle={t('confirmAction')}
        closeTitle={t('cancel')}
        onConfirm={() => {
          if (pendingSectionEdit) {
            confirmedSectionEditRef.current = pendingSectionEdit;
            setActiveFormSection(pendingSectionEdit);
            setPendingSectionEdit(undefined);
            return;
          }

          switch (confirmEditPopupSection) {
            case FORM_SECTION.DEPARTMENT:
              onChangeDepartmentValue(confirmEditValue);
              break;
            case FORM_SECTION.DOCTOR:
              onChangeCareProviderHid(confirmEditValue);
              break;
            case FORM_SECTION.SERVICE:
              onChangeServiceItemHid(confirmEditValue);
              break;
          }
          setConfirmEditValue('');
          setConfirmEditPopupSection(undefined);
        }}
        onClose={() => {
          setPendingSectionEdit(undefined);
          confirmedSectionEditRef.current = undefined;
          setConfirmEditValue('');
          setConfirmEditPopupSection(undefined);
        }}
      />
      {careProviderOptions?.some((o) => o.value === selectedCareProviderHid) && (
        <CareProviderInfoPopup
          isOpen={isCareProviderInfoPopupOpen}
          value={careProviderOptions.find((o) => o.value === selectedCareProviderHid)!}
          onClose={() => setCareProviderInfoPopupIsOpen(false)}
        />
      )}
    </>
  );
};

export default Form;
